import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, FileText, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Logo } from "@/components/site/Logo";
import { ProjectForm } from "@/components/admin/ProjectForm";
import { SiteForm } from "@/components/admin/SiteForm";
import { WebtoonsAdmin } from "@/components/admin/WebtoonsAdmin";
import { VideosAdmin } from "@/components/admin/VideosAdmin";
import { WipAdmin } from "@/components/admin/WipAdmin";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/hooks/useProjects";
import { api, asset, CATEGORY_LABEL, formatApiError } from "@/lib/api";

const Row = ({ p, onEdit, onDelete }) => (
  <div data-testid={`admin-row-${p.id}`} className="flex items-center gap-4 p-4 bg-[#12131A] border border-[#F5A524]/15 hover:border-[#F5A524]/60 transition-colors">
    <img src={asset(p.cover_url)} alt="" className="w-16 h-16 object-cover shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="font-display font-bold uppercase text-xl truncate flex items-center gap-2">
        {p.featured && <Star size={14} className="text-[#F5A524] fill-[#F5A524] shrink-0" />}{p.title}
      </p>
      <p className="font-mono text-[11px] tracking-[0.2em] text-zinc-400 flex gap-3 flex-wrap">
        <span className="text-[#F5A524]">{CATEGORY_LABEL[p.category]}</span><span>{p.year}</span>
        {p.pdf_url && <span className="flex items-center gap-1"><FileText size={11} /> PDF</span>}
      </p>
    </div>
    <Link to={`/project/${p.id}`} data-testid={`admin-view-${p.id}`} className="p-2 text-zinc-400 hover:text-[#F5A524]" aria-label="View"><ExternalLink size={16} /></Link>
    <button data-testid={`admin-edit-${p.id}`} onClick={() => onEdit(p)} className="p-2 text-zinc-300 hover:text-[#F5A524]" aria-label="Edit"><Pencil size={16} /></button>
    <button data-testid={`admin-delete-${p.id}`} onClick={() => onDelete(p)} className="p-2 text-zinc-300 hover:text-[#FF2A55]" aria-label="Delete"><Trash2 size={16} /></button>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { projects, loading, reload } = useProjects();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [tab, setTab] = useState("projects");

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  if (!user) return <main className="min-h-screen bg-[#0B0C10] grid place-items-center font-mono text-zinc-400">VERIFYING<span className="blink">_</span></main>;

  const confirmDelete = async () => {
    try {
      await api.delete(`/projects/${deleting.id}`);
      toast.success("PROJECT DELETED");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setDeleting(null);
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#0B0C10] text-zinc-100 border-t-2 border-[#F5A524]">
      <header className="flex items-center justify-between px-5 sm:px-10 py-5 border-b border-[#F5A524]/20">
        <Link to="/" data-testid="admin-home-link"><Logo /></Link>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline font-mono text-[11px] tracking-[0.2em] text-zinc-400">{user.email}</span>
          <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-zinc-300 hover:text-[#FF2A55]"><LogOut size={14} /> LOGOUT</button>
        </div>
      </header>
      <nav className="flex flex-wrap gap-2 px-5 sm:px-10 pt-8">
        {[["projects", "Projects"], ["webtoons", "Webtoons"], ["videos", "Timelapses"], ["wip", "WIP / Assets"], ["site", "Site Content"]].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`clip-tab font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors ${tab === k ? "bg-[#F5A524] text-black" : "bg-[#12131A] text-zinc-300 border border-[#F5A524]/20 hover:text-[#F5A524]"}`}>{l}</button>
        ))}
      </nav>
      {tab === "webtoons" && <WebtoonsAdmin />}
      {tab === "videos" && <VideosAdmin />}
      {tab === "wip" && <WipAdmin />}
      {tab === "site" && (
        <section className="px-5 sm:px-10 py-10 max-w-5xl">
          <p className="font-mono text-xs tracking-[0.35em] text-[#F5A524]">// PUBLIC COPY</p>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl mb-8">Site Content</h1>
          <SiteForm />
        </section>
      )}
      {tab === "projects" && (
      <section className="px-5 sm:px-10 py-10 max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-xs tracking-[0.35em] text-[#F5A524]">// CONTROL ROOM</p>
            <h1 className="font-display font-black uppercase text-5xl sm:text-6xl">Projects <span className="text-[#F5A524]">{projects.length}</span></h1>
          </div>
          <button data-testid="admin-new-project-btn" onClick={() => setEditing({})}
            className="clip-tab flex items-center gap-2 bg-[#F5A524] text-black font-mono font-bold text-sm tracking-[0.2em] px-6 py-3 hover:bg-white transition-colors">
            <Plus size={16} /> NEW PROJECT
          </button>
        </div>
        {loading ? <p className="font-mono text-zinc-400">LOADING...</p> : (
          <div className="space-y-3">
            {projects.map((p) => <Row key={p.id} p={p} onEdit={setEditing} onDelete={setDeleting} />)}
            {projects.length === 0 && <p className="font-mono text-zinc-400 border border-dashed border-[#F5A524]/30 p-10">NO PROJECTS YET.</p>}
          </div>
        )}
      </section>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="project-dialog" data-lenis-prevent className="bg-[#12131A] border-[#F5A524]/40 max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase text-3xl">{editing?.id ? "Edit Project" : "New Project"}</DialogTitle>
          </DialogHeader>
          {editing && <ProjectForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#12131A] border-[#FF2A55]/50 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase text-2xl">Delete "{deleting?.title}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">This removes the project from your portfolio. It can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn" className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={confirmDelete} className="rounded-none bg-[#FF2A55] text-black hover:bg-[#ff5577]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
